import { useState, useEffect, useCallback } from 'react';
import Display from '../Display';
import Chat from '../Chat';
import ChatInput from '../../ChatInput/ChatInput';
import { useChatbotStore, useChatStateStore, useMessagesStore, useProductsStore, useUserStore, useToastStore } from '../../zustand/store';
import { useHistoryUpdate, useLoadHistory, useSendPDPMessage, useSendMessageWithWebSocket } from '../../services/queries/chatbotQueries';
import { setColorCode } from '../../utils/utils';
import { sendInputFocusStatus } from '../../utils/utils';
import { handleConsoleTourMessage } from '../../utils/consoleTourHandler';
import { LayoutModeEnum, EnableModeEnum, DisplayContentModeEnum } from '../../type/modeState.type';
import { useAgentSocketStore } from '../../zustand/agentSocketStore';
import { StreamView } from '../ChatModal/StreamView';

const ChatFieldBase = (props) => {
    const { chatbot, setBootConfigGreetingComment } = useChatbotStore();
    const { products } = useProductsStore();
    const {
        isPreAnswered,
        displayLocation,
        setIsNewProduct,
        setIsChatbotPending,
        setEnableMode,
        setIsChatOpen,
        setIsStreaming,
        currentPage,
        setIsInConsoleTour,
        layoutMode,
        enableMode,
        autoUserMessage,
        setAutoUserMessage,
        clearAutoUserMessage,
        setIsFormSubmitted,
        setFormAutoStartOnce,
        setFastfiveScenarioId,
        setIsPreAnswered,
    } = useChatStateStore();
    const { 
        partnerType, 
        chatUserId, 
        partnerId,
        setIsMobileDevice, 
    } = useUserStore();
    const { setCustomizedGreeting } = useChatStateStore(); // 🧪 맞춤형 그리팅 설정 함수
    const { setToastMessage, setIsToastVisible } = useToastStore();
    const { refetch: refetchLoadHistory } = useLoadHistory();
    const [mode, setMode] = useState(DisplayContentModeEnum.GREETING);
    const [isDisplayVisible, setIsDisplayVisible] = useState(false);
    const { mutate: sendPDPMessage } = useSendPDPMessage();
    const { mutateAsync: updateHistory } = useHistoryUpdate();
    const { messages, streamMessage, setGuidelineContent, setGuidelineTitle, setGuidelineId } = useMessagesStore();
    const [visitedState, setVisitedState] = useState(null);
    const { mutate: sendMessageWithWebSocket } = useSendMessageWithWebSocket();
    const [isHistoryLoaded, setIsHistoryLoaded] = useState(false);

    useEffect(() => {
        const messageExistence = {
            type: 'messageExistence',
            messageExistenceState: messages.length > 0,
        }
        window.parent.postMessage(messageExistence, '*');
    }, [messages]);

    const handleConsoleTourMessageWrapper = (e) => {
        const { clearExamples } = useProductsStore.getState();
        handleConsoleTourMessage(e, {
            setIsStreaming,
            setIsChatbotPending,
            sendInputFocusStatus,
            currentPage,
            setIsInConsoleTour,
            clearExamples,
            sendMessageWithWebSocket
        });
    };

    useEffect(() => {
        window.addEventListener('message', handleConsoleTourMessageWrapper);
        return () => {
            window.removeEventListener('message', handleConsoleTourMessageWrapper);
        };
    }, []);

    const handleMessage = useCallback((e) => {
        const {
            currentPage,
            buttonClickState,
            clickedElement,
            enableMode: receivedEnableMode,
            addedProductToCart,
            customizedGreeting,
            requestMessage,
            addedProductWithOptionsToCart,
            addProductToCartFailed,
            bootConfigGreetingComment,
            messageType,
            contentData,
            autoUserMessage,
            entryScenarioKey,   // fastfive: SDK 가 현재 페이지 컨텍스트로 보내는 시나리오 자동 진입 키
        } = e.data;
        const currentEnableMode = useChatStateStore.getState().enableMode;
        
        // SDK에서 받은 문자열을 Enum으로 매핑
        const enableModeMap = {
            'full': EnableModeEnum.FULL,
            'shrink': EnableModeEnum.SHRINK,
        };
        
        const mappedEnableMode = enableModeMap[receivedEnableMode];
        
        if (mappedEnableMode && currentEnableMode !== mappedEnableMode) {
            setEnableMode(mappedEnableMode);
        }

        if (customizedGreeting) {
            setCustomizedGreeting(customizedGreeting);
        }

        if (bootConfigGreetingComment) {
            setBootConfigGreetingComment(bootConfigGreetingComment);
        }

        if (autoUserMessage) {
            setAutoUserMessage(autoUserMessage);
        }

        // fastfive: SDK 에서 현재 페이지 컨텍스트에 맞는 시나리오를 보내면 entry 단계를 건너뛰고 바로 PreQuestion 진입.
        // - 유효하지 않은 key 면 Chat.jsx 의 self-healing (selectedScenario === null) 으로 entry 버튼이 다시 노출됨.
        // - SDK postMessage 로 진입 → auto-entered=true (scenario greeting 노출 대상)
        if (entryScenarioKey && partnerType === 'fastfive') {
            setFastfiveScenarioId(entryScenarioKey, true);
            setIsPreAnswered(false);
        }

        // 플로팅 버튼 클릭
        if ((buttonClickState && clickedElement === 'floatingContainer') || clickedElement === 'carouselRedirect') {
            updateHistory({ url: currentPage, itemId: '' })
                .then((visitedState) => {
                    setVisitedState(visitedState);
                    if (displayLocation === 'PRODUCT_DETAIL' && visitedState) {
                        if (visitedState === 'FIRST_VISIT') {
                            setIsChatbotPending(true);
                            sendPDPMessage({ chatType: 'default' });
                        } else if (visitedState === 'REVISIT') {
                            setIsChatbotPending(true);
                            sendPDPMessage({ chatType: 'revisit' });
                        }
                    }
                });
            setIsChatOpen(true);
        }

        // 닫기 버튼 클릭
        if (buttonClickState && (clickedElement === 'closeButtonContainer' || clickedElement === 'closeActionArea')) {
            setIsNewProduct(false);
            setIsChatOpen(false);
        }

        // 장바구니 상품 담기 성공
        if (addedProductToCart) {
            setToastMessage('장바구니에 상품을 담았어요');
            setIsToastVisible(true);
        }

        // 장바구니 상품 담기 성공
        if (addedProductWithOptionsToCart) {
            setToastMessage('장바구니에 상품을 담았어요');
            setIsToastVisible(true);
        }

        // 장바구니 상품 담기 실패
        if (addProductToCartFailed) {
            setToastMessage('로그인 후 다시 시도해주세요!');
            setIsToastVisible(true);
        }

        // [modal] 메세지 전송 요청 from SDK
        if (buttonClickState && clickedElement === 'sendButton') {
            setIsStreaming(true);
            setIsChatbotPending(true);
            sendMessageWithWebSocket({ messageInput: requestMessage, isChatbot: false, preAnswers: [] });
        }

        // [modal] 고객 센터 문의 from SDK
        if (buttonClickState && clickedElement === 'csInquiryButton') {
            setTimeout(() => {
                setFormAutoStartOnce(true);
                setIsFormSubmitted('ready');
            }, 1000);
        }

        if (messageType === 'gentoo-statics') {
            const previousStatics = JSON.parse(sessionStorage.getItem('gentoo-statics') || '{}');
            sessionStorage.setItem('gentoo-statics', JSON.stringify({
                ...previousStatics,
                ...contentData,
            }));
        }

    }, [displayLocation]);

    useEffect(() => {
        window.addEventListener('message', handleMessage);
        return () => {
            window.removeEventListener('message', handleMessage);
        }
    }, [handleMessage])

    // Ensure chatInput is functional, but note that streaming chat is currently non-operational
    useEffect(() => {
        setIsStreaming(false);
        setIsChatbotPending(false);
    }, []);

    useEffect(() => {
        if (partnerType === 'fastfive' && !isPreAnswered) {
            setIsDisplayVisible(false);
        } else if (messages.length === 0 && streamMessage.length === 0) {
            setIsDisplayVisible(false);
        } else {
            setIsDisplayVisible(true);
        }
    }, [messages, streamMessage, isPreAnswered]);

    // chatbot 로드 시 히스토리 로드 + autoUserMessage 처리 (순서 보장)
    useEffect(() => {
        const init = async () => {
            await refetchLoadHistory();
            setIsHistoryLoaded(true);
        };
        
        if (chatbot) {
            init();
            setColorCode(chatbot?.colorCode);
            setIsMobileDevice(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
        }
    }, [chatbot]);

    useEffect(() => {
        setTimeout(() => {
            if (isHistoryLoaded && autoUserMessage) {
                setIsStreaming(true);
                setIsChatbotPending(true);
                sendMessageWithWebSocket({
                    messageInput: autoUserMessage,
                    isChatbot: false,
                    preAnswers: []
                });
                clearAutoUserMessage();
            }
        }, 0);
    }, [autoUserMessage, isHistoryLoaded]);

    useEffect(() => {
        if (products.length > 0) {
            if (partnerType === 'fastfive') {
                setMode(DisplayContentModeEnum.PRODUCT_VERTICAL_LIST);
            } else if ((layoutMode === LayoutModeEnum.MODAL) && (enableMode === EnableModeEnum.SHRINK)) {
                setMode(DisplayContentModeEnum.SIMPLE_LIST);
            } else {
                setMode(chatbot?.carouselType === 'single' ? 
                    DisplayContentModeEnum.PRODUCT_SINGLE : 
                    (chatbot?.carouselType === 'carousel-horizontal' ? 
                        DisplayContentModeEnum.PRODUCT_HORIZONTAL_LIST : 
                        DisplayContentModeEnum.PRODUCT_VERTICAL_LIST
                    ) || DisplayContentModeEnum.PRODUCT_SINGLE
                );
            }
        } else {
            setMode(DisplayContentModeEnum.GREETING);
        }
    }, [products, layoutMode, enableMode]);
    
    useEffect(() => {
        const handler = (event) => {
            if (event.data.type === 'guidelineUpdate') {
                setGuidelineId(event.data.id);
                setGuidelineContent(event.data.content);
                setGuidelineTitle(event.data.title);
            }
        };

        window.addEventListener('message', handler);
        return () => {
            window.removeEventListener('message', handler);
        };
    }, []);

    const connect = useAgentSocketStore((s) => s.connect);
    
    useEffect(() => {
        connect({
            action: "register",
            chatUserId: chatUserId,
            // chatUserId: 'tTrifHIpxtzgs6HK3w8NeLa22VSU443c',
            externalKey: partnerId
        }, displayLocation);
    }, [connect,chatUserId, partnerId]);

    return (
        <div style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            {
                layoutMode === LayoutModeEnum.MODAL && enableMode === EnableModeEnum.SHRINK ?
                <StreamView /> :
                <Chat />
            }
            {/* {
                layoutMode === LayoutModeEnum.MODAL && enableMode === EnableModeEnum.SHRINK && (isChatbotPending || isStreaming) 
                    ? null 
                    : isDisplayVisible && <Display mode={mode} />
            } */}
            {
                isDisplayVisible && <Display mode={mode} />
            }
            {
                Object.keys(chatbot.preQuestion.questions).length === 0 || isPreAnswered ?
                    <ChatInput /> : partnerType === 'fastfive' ?
                    <ChatInput resetOnly /> :
                    <div style={{ width: '100%', height: '86px' }}>
                    </div>
            }
        </div>
    );
};

export default ChatFieldBase
